import { useEffect, useLayoutEffect, useState, type RefObject } from "react";

// ── Film grain for the brand surfaces (home gradient, compact header) ─────────
// A flat digital gradient reads as plastic, and a long fade on a dark brand
// colour shows bands. Print and film carry grain, which breaks the bands up,
// so the brand surface carries a tile of fractal noise blended with OVERLAY:
// neutral on 50% grey, strongest on mid-tones, fading out towards black and
// white, so the average colour stays where it was and only texture is added.
//
// The surface is ONE OPAQUE IMAGE, computed here and set as the element's own
// background: the vertical gradient (brand colour fading into the panel
// colour) with the noise overlaid, pixel for pixel the arithmetic the CSS
// blend produced. One 160 px wide strip the height of the element, repeated
// across. Nothing about it reaches the compositor: no extra element, no
// z-index, no isolation, no mix-blend-mode, no opacity layer. The first
// version (a live SVG filter + mix-blend-mode on a layer of its own) cost the
// GPU 60-150 ms whenever the home screen's layer was created or torn down,
// which is every view switch, and dropped a dozen frames at the start of the
// cross-fade.
//
// Strips are rendered once per colour + height + pixel density and cached as
// object URLs; the two panel sizes a page can need are rendered when the
// widget mounts (warmGrainBackdrop), so the home screen has its texture on
// its first frame. An uncached size shows the plain gradient for a frame or
// two and then the strip.
export const GRAIN_TILE_PX = 160;
const GRAIN_OPACITY = 0.3;

const NOISE_SVG =
  `<svg xmlns='http://www.w3.org/2000/svg' width='${GRAIN_TILE_PX}' height='${GRAIN_TILE_PX}'>` +
  "<filter id='g' color-interpolation-filters='sRGB'>" +
  "<feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/>" +
  "<feColorMatrix values='1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 0 0 0 0 1'/>" +
  "</filter><rect width='100%' height='100%' filter='url(#g)'/></svg>";
const NOISE_SVG_URL = `data:image/svg+xml,${encodeURIComponent(NOISE_SVG)}`;

// ── The noise itself: one grey tile per page, at the screen's pixel density ──
type Noise = { size: number; scale: number; grey: Uint8ClampedArray };
let noise: Noise | null = null;
let noiseLoading = false;
const noiseWaiters = new Set<() => void>();

function loadNoise(): void {
  if (noise || noiseLoading || typeof document === "undefined") return;
  noiseLoading = true;
  try {
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(3, Math.max(1, Math.round(window.devicePixelRatio || 1)));
        const size = GRAIN_TILE_PX * scale;
        const canvas = document.createElement("canvas");
        canvas.width = canvas.height = size;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, size, size);
        const px = ctx.getImageData(0, 0, size, size).data;
        const grey = new Uint8ClampedArray(size * size);
        for (let i = 0, j = 0; i < px.length; i += 4, j++) grey[j] = px[i];
        noise = { size, scale, grey };
        noiseWaiters.forEach((w) => w());
        noiseWaiters.clear();
      } catch {
        // No canvas (privacy mode, old engine): surfaces stay plain gradients.
      }
    };
    img.src = NOISE_SVG_URL;
  } catch {
    // Same: plain gradients.
  }
}

// ── Colours ──────────────────────────────────────────────────────────────────
function parseColor(input: string): [number, number, number] | null {
  const s = input.trim();
  const hex = s.match(/^#([0-9a-f]{3,8})$/i)?.[1];
  if (hex) {
    if (hex.length === 3 || hex.length === 4)
      return [parseInt(hex[0] + hex[0], 16), parseInt(hex[1] + hex[1], 16), parseInt(hex[2] + hex[2], 16)];
    if (hex.length === 6 || hex.length === 8)
      return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
    return null;
  }
  const rgb = s.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
}

/** The CSS gradient the strip reproduces (and the fallback while it renders). */
export function grainGradientCss(homeColor: string, panelColor: string): string {
  return `linear-gradient(180deg, ${homeColor} 0%, ${homeColor}80 40%, ${homeColor}20 65%, transparent 100%), ${panelColor}`;
}

// Alpha of the brand colour at a vertical fraction, matching the stops above
// (1 → 0.5 at 40% → 0.125 at 65% → 0 at 100%).
function gradientAlpha(t: number): number {
  if (t <= 0.4) return 1 - (t / 0.4) * 0.5;
  if (t <= 0.65) return 0.5 - ((t - 0.4) / 0.25) * 0.375;
  return 0.125 - ((t - 0.65) / 0.35) * 0.125;
}

// ── Strips ───────────────────────────────────────────────────────────────────
export type GrainSpec = {
  homeColor: string;
  panelColor: string;
  gradient: boolean;
  /** Element height in its own CSS px. */
  cssHeight: number;
  /** Device pixels per CSS px of the element (DPR × any CSS zoom). */
  scale: number;
};

const keyOf = (s: GrainSpec) =>
  `${s.homeColor}|${s.panelColor}|${s.gradient ? "g" : "f"}|${Math.round(s.cssHeight)}|${s.scale.toFixed(2)}`;

const strips = new Map<string, string>(); // key → object URL
const pending = new Map<string, Set<() => void>>();
const MAX_STRIPS = 8;

function renderStrip(spec: GrainSpec, key: string): void {
  if (!noise || typeof document === "undefined") return;
  const home = parseColor(spec.homeColor);
  const panel = parseColor(spec.panelColor);
  if (!home || !panel) return;
  const w = Math.max(1, Math.round(GRAIN_TILE_PX * spec.scale));
  const h = Math.max(1, Math.round(spec.cssHeight * spec.scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const out = ctx.createImageData(w, h);
  const px = out.data;
  const { size, scale: nscale, grey } = noise;
  // Noise sampled at its own density, so the grain is as fine as the SVG was.
  const nx = new Int32Array(w);
  for (let x = 0; x < w; x++) nx[x] = Math.floor((x / spec.scale) * nscale) % size;
  const cb = [0, 0, 0];
  for (let y = 0; y < h; y++) {
    const a = spec.gradient ? gradientAlpha(h <= 1 ? 0 : y / (h - 1)) : 1;
    for (let c = 0; c < 3; c++) cb[c] = (a * home[c] + (1 - a) * panel[c]) / 255;
    const ny = (Math.floor((y / spec.scale) * nscale) % size) * size;
    let o = y * w * 4;
    for (let x = 0; x < w; x++) {
      const n = grey[ny + nx[x]] / 255;
      for (let c = 0; c < 3; c++) {
        const b = cb[c];
        const overlay = b <= 0.5 ? 2 * b * n : 1 - 2 * (1 - b) * (1 - n);
        px[o + c] = Math.round(255 * (b + GRAIN_OPACITY * (overlay - b)));
      }
      px[o + 3] = 255;
      o += 4;
    }
  }
  ctx.putImageData(out, 0, 0);
  canvas.toBlob((blob) => {
    if (!blob) return;
    if (strips.size >= MAX_STRIPS) {
      const oldest = strips.keys().next().value;
      if (oldest !== undefined) {
        URL.revokeObjectURL(strips.get(oldest)!);
        strips.delete(oldest);
      }
    }
    strips.set(key, URL.createObjectURL(blob));
    pending.get(key)?.forEach((w) => w());
    pending.delete(key);
  }, "image/png");
}

function ensureStrip(spec: GrainSpec, onReady?: () => void): string | null {
  const key = keyOf(spec);
  const url = strips.get(key);
  if (url) return url;
  if (onReady) {
    if (!pending.has(key)) pending.set(key, new Set());
    pending.get(key)!.add(onReady);
  }
  if (pending.has(key) && pending.get(key)!.size > (onReady ? 1 : 0)) return null; // already rendering
  if (!pending.has(key)) pending.set(key, new Set());
  if (!noise) {
    loadNoise();
    const run = () => renderStrip(spec, key);
    noiseWaiters.add(run);
    return null;
  }
  renderStrip(spec, key);
  return null;
}

/** CSS `background` for a ready strip. */
export function grainBackground(url: string, panelColor: string): string {
  return `url("${url}") left top / ${GRAIN_TILE_PX}px 100% repeat-x, ${panelColor}`;
}

/** Render a strip ahead of time (the widget calls this for the sizes a page can need). */
export function warmGrainBackdrop(spec: GrainSpec): void {
  if (spec.cssHeight <= 0) return;
  ensureStrip(spec);
}

/**
 * The grained background for `ref`'s element, or null while it renders
 * (callers fall back to `grainGradientCss`). Re-renders when the element's
 * height or the colours change.
 */
export function useGrainBackdrop(
  ref: RefObject<HTMLElement>,
  opts: { enabled: boolean; homeColor: string; panelColor: string; gradient: boolean },
): string | null {
  const [measure, setMeasure] = useState<{ cssHeight: number; scale: number } | null>(null);
  const [, bump] = useState(0);

  useLayoutEffect(() => {
    if (!opts.enabled) return;
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const read = () => {
      const cssHeight = el.offsetHeight;
      // CSS zoom up the ancestry (the desktop panel renders at 0.88). Read from
      // computed style, never from bounding rects: the panel is mid-scale while
      // it springs open, and a rect-derived zoom would miss the pre-rendered key.
      let zoom = 1;
      for (let e: HTMLElement | null = el; e; e = e.parentElement) {
        const z = parseFloat(getComputedStyle(e).zoom || "1");
        if (z && z !== 1) zoom *= z;
      }
      const scale = (window.devicePixelRatio || 1) * zoom;
      setMeasure((m) =>
        m && Math.round(m.cssHeight) === Math.round(cssHeight) && m.scale.toFixed(2) === scale.toFixed(2)
          ? m
          : { cssHeight, scale },
      );
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, opts.enabled]);

  const spec: GrainSpec | null =
    opts.enabled && measure && measure.cssHeight > 0
      ? { homeColor: opts.homeColor, panelColor: opts.panelColor, gradient: opts.gradient, ...measure }
      : null;
  const url = spec ? strips.get(keyOf(spec)) ?? null : null;

  useEffect(() => {
    if (!spec || url) return;
    let alive = true;
    ensureStrip(spec, () => {
      if (alive) bump((n) => n + 1);
    });
    return () => {
      alive = false;
    };
    // The key captures every input of the strip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec ? keyOf(spec) : null, url]);

  return url ? grainBackground(url, opts.panelColor) : null;
}

loadNoise();
